import React, { createContext, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { type SupplierFormData, type TabProps } from './types';
import { supplierFormResolver } from './validation';

interface SupplierFormContextValue {
  mode: TabProps['mode'];
  supplier: TabProps['supplier'];
  formMethods: ReturnType<typeof useForm<SupplierFormData>>;
}

const SupplierFormContext = createContext<SupplierFormContextValue | null>(
  null
);

export const useSupplierForm = () => {
  const context = useContext(SupplierFormContext);
  if (!context) {
    throw new Error(
      'useSupplierForm must be used within a SupplierFormProvider'
    );
  }
  return context;
};

interface SupplierFormProviderProps extends TabProps {
  children: React.ReactNode;
}

const extractMinOrderValue = (
  minOrder?: { price?: { amount?: number | null } | null } | null
): string => {
  if (!minOrder?.price?.amount) return '';
  // Convert cents to whole units (e.g., 1200 -> "12")
  return (minOrder.price.amount / 100).toString();
};

export const SupplierFormProvider: React.FC<SupplierFormProviderProps> = ({
  mode,
  supplier,
  children,
}) => {
  // eslint-disable-next-line no-console
  console.log('Supplier data:', {
    displayInMarketplace: supplier?.priceSetting?.displayInMarketplace,
    priceSetting: supplier?.priceSetting,
    supplier,
  });
  // eslint-disable-next-line no-console
  console.log('Setting catalog defaults:', {
    defaultSubCatalog: supplier?.defaultSubCatalog,
    noDefaultCatalog: supplier?.noDefaultCatalog,
    displayInMarketplace: supplier?.priceSetting?.displayInMarketplace,
    displayToCustomers: supplier?.priceSetting?.displayToCustomers,
    hideMyPrice: supplier?.hideMyPrice,
  });
  // eslint-disable-next-line no-console
  console.log('SupplierFormProvider - Raw supplier data:', {
    supplier,
    priceSetting: supplier?.priceSetting,
    hideMyPrice: supplier?.hideMyPrice,
  });

  const formMethods = useForm<SupplierFormData>({
    resolver: supplierFormResolver,
    mode: 'onChange',
    defaultValues: {
      // Profile defaults
      name: supplier?.name ?? '',
      description: supplier?.description ?? '',
      primaryCategory: supplier?.primaryAssortment ?? '',
      secondaryCategories: supplier?.secondaryAssortments ?? [],
      deliveryFee: supplier?.deliveryCosts ?? '',
      minimumOrderValue: extractMinOrderValue(supplier?.minOrder),
      minimumOrderCurrency: supplier?.minOrder?.price?.currency ?? 'EUR',

      // Catalog defaults
      supplierDefaultSubCatalog: supplier?.defaultSubCatalog ?? '',
      displayInMarketplace:
        supplier?.priceSetting?.displayInMarketplace ?? false,
      displayToCustomers: supplier?.priceSetting?.displayToCustomers ?? false,
      supplierHideMyPrice: supplier?.hideMyPrice ?? false,

      // Order preferences defaults
      allowCustomProducts: supplier?.allowCustomProducts ?? false,
      mandatoryCustomerNumber: supplier?.customerNumberMandatory ?? false,
      mandatoryDeliveryDate: supplier?.deliveryDateMandatory ?? true,
      enableProductComments: supplier?.allowBuyerComment ?? false,
      allowFractionalUnits: supplier?.hasEnabledFractionalUnits ?? false,
      allowOnlyOneVariant: supplier?.allowOnlyOneActiveVariant ?? false,
      sortBy:
        supplier?.sortCartBy === 'alphabetical' ? 'alphabetical' : 'orderGuide',
      cutoffType: 'strict',
      deliveryDays: [],
      cutoffTimes: {},
    },
  });

  return (
    <SupplierFormContext.Provider value={{ mode, supplier, formMethods }}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </SupplierFormContext.Provider>
  );
};
